'use client';

/**
 * VerticalFeatures — shared "what SUM actually does" capability section.
 *
 * Two shapes, controlled by whether `copy.rich` is provided:
 *
 *   1. Simple (events / b2b): 4 capability cards on `.sum-caps-grid`.
 *      Each card is glyph + tag + title + body. No media, no
 *      auto-pilot mockup, no growth toolkit.
 *
 *   2. Rich (retail / services): 3–4 capability cards (with optional
 *      per-card media), an Auto-Pilot card with a custom approval-
 *      moment mockup, a "growth toolkit" sub-block with two panels
 *      (one of which can be the animated Google review proof), and a
 *      quiet "go deeper" link row.
 *
 * Section number is a prop so each page can place this section at its
 * right number; the background is fixed at #0A0E27.
 */

import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import SectionLabel from '../../home/SectionLabel';
import { Locale } from '../../../../../i18n-config';
import type { CapStyle, FeaturesCopy, GrowthPanel } from './types';

type Props = {
  sectionNum: string;
  /** Locale used for go-deeper link prefixing. */
  lang: Locale;
  copy: FeaturesCopy;
};

// Style tokens — recur across the rich block.
const TXT = '#F5F7FA';
const SUB = '#B4C1D8';
const MUTED = '#6B7593';
const GHOST = '#3A3F5C';
const SURFACE = 'rgba(255,255,255,0.03)';
const BORDER = 'rgba(255,255,255,0.08)';
const BORDER_DIM = 'rgba(255,255,255,0.06)';
const MONO: CSSProperties = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

/**
 * Animated 5-star fill used inside the "reviews" growth panel.
 *
 * Stars 1 and 2 are always shown (the implied "before SUM" baseline).
 * Stars 3, 4, 5 fade + scale in on each cycle, then fade back to the
 * 2-star baseline. The "rating" number fades in once and stays.
 *
 * The loop pauses when out of viewport (no off-screen CPU). Honors
 * prefers-reduced-motion: under that media query, all 5 stars and the
 * rating are immediately at rest.
 */
const STAR_CYCLE_MS = 3000;

const GoogleReviewProof = ({ rating, alt }: { rating: string; alt: string }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [tick, setTick] = useState(0);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMq.matches) {
      setReduceMotion(true);
      setHasPlayedOnce(true);
      return;
    }
    const node = ref.current;
    if (!node) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (intervalId) return;
      setTick((t) => t + 1);
      setHasPlayedOnce(true);
      intervalId = setInterval(() => setTick((t) => t + 1), STAR_CYCLE_MS);
    };
    const stop = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) start();
          else stop();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.2 }
    );
    obs.observe(node);
    return () => {
      stop();
      obs.disconnect();
    };
  }, []);

  const [filled, setFilled] = useState(false);
  const [popping, setPopping] = useState(false);
  useEffect(() => {
    if (!hasPlayedOnce && !reduceMotion) return;
    setFilled(false);
    setPopping(false);
    let r1 = 0;
    let r2 = 0;
    r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => setFilled(true));
    });
    // Pop fires once the 5th (last) star has arrived: enterDelay for star idx 4
    // is 200ms, the entrance transition is 220ms — so peak is ~420ms in.
    const popInAt = 420;
    const popOutAt = popInAt + 180;
    const popInTimer = setTimeout(() => setPopping(true), popInAt);
    const popOutTimer = setTimeout(() => setPopping(false), popOutAt);
    const fadeOutAt = 420 + 1500;
    const fadeTimer = setTimeout(() => setFilled(false), fadeOutAt);
    return () => {
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      clearTimeout(popInTimer);
      clearTimeout(popOutTimer);
      clearTimeout(fadeTimer);
    };
  }, [tick, hasPlayedOnce, reduceMotion]);

  const ratingVisible = reduceMotion || hasPlayedOnce;

  return (
    <div
      ref={ref}
      className="mt-2 flex items-center justify-between gap-3 rounded-[10px] px-3.5 pt-3.5 pb-3"
      style={{
        background: 'rgba(10,14,39,0.5)',
        border: '1px solid rgba(255,255,255,0.06)',
        minHeight: 64,
      }}
      aria-label={alt}
    >
      <div
        className="flex items-center justify-center rounded-md"
        style={{ background: '#FFFFFF', padding: '6px 8px', width: 122 }}
      >
        <Image
          src="https://cdn.1cdn.app/application/UMD/25083018084491_google_my_business.png"
          alt="Google Business Profile"
          width={1920}
          height={488}
          sizes="106px"
          className="block h-auto"
          style={{ width: '100%' }}
        />
      </div>
      <div className="flex items-center gap-1.5" aria-hidden="true">
        <div className="flex">
          {[0, 1, 2, 3, 4].map((i) => {
            const isBaseline = i < 2;
            const visible = isBaseline ? true : reduceMotion ? true : filled;
            const animIndex = i - 2;
            const enterDelay = animIndex * 100;
            const exitDelay = (2 - animIndex) * 40;
            const isPopping = !reduceMotion && popping && filled;
            const scale = !visible ? 0.6 : isPopping ? 1.15 : 1;
            const baselineTransition = isPopping ? 'transform 140ms ease-out' : 'transform 200ms ease-out';
            return (
              <span
                key={i}
                style={{
                  fontSize: 16,
                  lineHeight: 1,
                  color: '#FFB400',
                  opacity: visible ? 1 : 0,
                  transform: `scale(${scale})`,
                  transition: isBaseline
                    ? baselineTransition
                    : visible
                    ? isPopping
                      ? `transform 140ms ease-out, opacity 220ms ease-out ${enterDelay}ms`
                      : `opacity 220ms ease-out ${enterDelay}ms, transform 200ms ease-out`
                    : `opacity 300ms ease-in ${exitDelay}ms, transform 300ms ease-in ${exitDelay}ms`,
                  marginRight: i < 4 ? 1 : 0,
                }}
              >
                ★
              </span>
            );
          })}
        </div>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#F5F7FA',
            opacity: ratingVisible ? 1 : 0,
            transition: 'opacity 240ms ease-out 380ms',
          }}
        >
          {rating}
        </span>
      </div>
    </div>
  );
};

/** Convert a hex like '#A78BFA' to 'r,g,b'. Used to tint the CTA panel's
 *  background and border in the growth toolkit. */
const hexToRgb = (hex: string): string => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `${r},${g},${b}`;
};

const GrowthPanelView = ({ panel, lang }: { panel: GrowthPanel; lang: Locale }) => {
  if (panel.kind === 'reviews') {
    return (
      <div
        className="flex flex-col gap-2.5 rounded-2xl p-5 pb-6"
        style={{ background: SURFACE, border: `1px solid ${BORDER}` }}
      >
        <div style={{ ...MONO, fontSize: 10, letterSpacing: 2, color: '#22D3EE' }}>{panel.tag}</div>
        <h4 className="m-0 font-bold" style={{ fontSize: 18, letterSpacing: '-0.01em', color: TXT }}>
          {panel.title}
        </h4>
        <p className="m-0" style={{ fontSize: 14, lineHeight: 1.55, color: SUB }}>
          {panel.body}
        </p>
        <GoogleReviewProof rating={panel.rating} alt={panel.ratingAriaLabel} />
      </div>
    );
  }
  // CTA panel
  const rgb = hexToRgb(panel.cta.accent);
  // The link href is page-relative when it starts with `/`; we prefix the
  // locale segment so it works in the App Router's `[locale]` tree.
  const href = panel.cta.href.startsWith('/') ? `/${lang}${panel.cta.href}` : panel.cta.href;
  return (
    <div
      className="flex flex-col gap-2.5 rounded-2xl p-5 pb-6"
      style={{ background: SURFACE, border: `1px solid ${BORDER}` }}
    >
      <div style={{ ...MONO, fontSize: 10, letterSpacing: 2, color: panel.cta.accent }}>{panel.tag}</div>
      <h4 className="m-0 font-bold" style={{ fontSize: 18, letterSpacing: '-0.01em', color: TXT }}>
        {panel.title}
      </h4>
      <p className="m-0" style={{ fontSize: 14, lineHeight: 1.55, color: SUB }}>
        {panel.body}
      </p>
      {panel.image ? (
        <div
          className="mt-2 flex flex-col items-stretch gap-3 rounded-[10px] px-3.5 pt-3.5 pb-3 sm:flex-row sm:items-center sm:justify-between"
          style={{
            background: 'rgba(10,14,39,0.5)',
            border: '1px solid rgba(255,255,255,0.06)',
            minHeight: 64,
          }}
        >
          <div
            className="flex items-center justify-center rounded-md self-start sm:self-auto"
            style={{ background: '#FFFFFF', padding: '6px 8px', width: 122 }}
          >
            <Image
              src={panel.image.src}
              alt={panel.image.alt}
              width={1920}
              height={488}
              sizes="106px"
              className="block h-auto"
              style={{ width: '100%' }}
            />
          </div>
          <a
            href={href}
            className="inline-flex items-center justify-center gap-2 rounded-lg transition-colors"
            style={{
              background: `rgba(${rgb},0.08)`,
              border: `1px solid rgba(${rgb},0.35)`,
              color: panel.cta.accent,
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            <span>{panel.cta.label}</span>
            <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
              →
            </span>
          </a>
        </div>
      ) : (
        <div className="mt-2 flex justify-end">
          <a
            href={href}
            className="inline-flex items-center gap-2 rounded-lg transition-colors"
            style={{
              background: `rgba(${rgb},0.08)`,
              border: `1px solid rgba(${rgb},0.35)`,
              color: panel.cta.accent,
              padding: '10px 16px',
              fontSize: 13,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            <span>{panel.cta.label}</span>
            <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>
              →
            </span>
          </a>
        </div>
      )}
    </div>
  );
};

const VerticalFeatures = ({ sectionNum, lang, copy }: Props) => {
  return (
    <section
      id={copy.anchorId}
      className="px-6 py-24 lg:py-28"
      style={{ background: '#0A0E27', borderTop: '1px solid rgba(255,255,255,0.05)', color: TXT }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={sectionNum} label={copy.label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 48px',
            maxWidth: 820,
          }}
        >
          {copy.headlineA} <span style={{ color: SUB }}>{copy.headlineB}</span>
        </h2>

        <div className="sum-caps-grid">
          {copy.cards.map((f) => (
            <div key={f.key} className="sum-cap-card" style={{ '--cap-accent': f.accent } as CapStyle}>
              <div className="sum-cap-glyph" aria-hidden="true">
                {f.glyph}
              </div>
              <div className="sum-cap-tag">{f.tag}</div>
              <div className="sum-cap-title">{f.title}</div>
              <div className="sum-cap-body">{f.body}</div>
              {f.media && (
                <div
                  className="mt-3.5 overflow-hidden rounded-xl p-2"
                  style={{
                    border: `1px solid ${BORDER_DIM}`,
                    background: 'linear-gradient(180deg, rgba(245,247,250,0.96) 0%, rgba(225,230,240,0.96) 100%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
                  }}
                >
                  <Image
                    src={f.media.src}
                    alt={f.media.alt}
                    width={520}
                    height={520}
                    sizes="(max-width: 720px) 100vw, 340px"
                    unoptimized={f.media.unoptimized}
                    className="block w-full h-auto rounded-md"
                  />
                </div>
              )}
            </div>
          ))}

          {copy.rich &&
            (() => {
              const ap = copy.rich.autoPilot;
              const apRgb = hexToRgb(ap.accent);
              return (
                <div key="auto-pilot" className="sum-cap-card" style={{ '--cap-accent': ap.accent } as CapStyle}>
                  <div className="sum-cap-glyph" aria-hidden="true">
                    {ap.glyph}
                  </div>
                  <div className="sum-cap-tag">{ap.tag}</div>
                  <div className="sum-cap-title">{ap.title}</div>
                  <div className="sum-cap-body">{ap.body}</div>

                  <div
                    className="mt-3.5 aspect-square flex flex-col gap-3 rounded-xl p-4"
                    style={{
                      background: `linear-gradient(180deg, rgba(${apRgb},0.08) 0%, rgba(${apRgb},0.02) 100%)`,
                      border: `1px solid rgba(${apRgb},0.18)`,
                    }}
                    aria-hidden="true"
                  >
                    <div className="px-1">
                      <div
                        style={{
                          ...MONO,
                          fontSize: 11,
                          fontWeight: 500,
                          letterSpacing: 1.5,
                          color: ap.accent,
                        }}
                      >
                        {ap.mockup.kickerLine1}
                      </div>
                      <div style={{ ...MONO, fontSize: 10, letterSpacing: 1, color: SUB, marginTop: 2 }}>
                        {ap.mockup.kickerLine2}
                      </div>
                    </div>

                    <div
                      className="flex-1 rounded-[10px] px-3 pt-3 pb-2.5 flex flex-col"
                      style={{
                        background: 'rgba(10,14,39,0.7)',
                        border: `1px solid ${BORDER_DIM}`,
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: TXT,
                      }}
                    >
                      <div className="flex-1">{ap.mockup.body}</div>
                      <div className="mt-1.5" style={{ ...MONO, fontSize: 10, color: MUTED }}>
                        {ap.mockup.meta}
                      </div>
                    </div>

                    <div
                      className="self-start rounded-full px-2.5 py-1"
                      style={{
                        background: `rgba(${apRgb},0.12)`,
                        border: `1px solid rgba(${apRgb},0.3)`,
                        color: ap.accent,
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                      }}
                    >
                      {ap.mockup.statusPill}
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>

        {copy.rich && (
          <>
            {/* Growth toolkit */}
            <div className="mt-14 pt-10" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="mb-2" style={{ ...MONO, fontSize: 11, letterSpacing: 2, color: MUTED }}>
                {copy.rich.growth.kicker}
              </div>
              <h3
                className="m-0 mb-1.5 font-bold"
                style={{
                  fontSize: 'clamp(22px, 2.4vw, 28px)',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.2,
                  color: TXT,
                  maxWidth: 720,
                }}
              >
                {copy.rich.growth.headline}
              </h3>
              <p className="m-0 mb-6" style={{ color: SUB, fontSize: 15, lineHeight: 1.5, maxWidth: 720 }}>
                {copy.rich.growth.sub}
              </p>

              <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                {copy.rich.growth.panels.map((p, i) => (
                  <GrowthPanelView key={`${p.kind}-${i}`} panel={p} lang={lang} />
                ))}
              </div>
            </div>

            {/* Quiet "go deeper" link row */}
            <div
              className="mt-9 flex flex-wrap items-center"
              style={{ ...MONO, gap: '8px 18px', fontSize: 13, color: MUTED, letterSpacing: 1.2 }}
            >
              <span className="uppercase" style={{ fontSize: 11 }}>
                {copy.rich.goDeeper.label}
              </span>
              {copy.rich.goDeeper.links.map((l, i) => (
                <React.Fragment key={l.href}>
                  <a
                    href={`/${lang}${l.href}`}
                    className="pb-px transition-colors hover:!text-[#22D3EE]"
                    style={{
                      color: SUB,
                      textDecoration: 'none',
                      borderBottom: '1px dashed rgba(180,193,216,0.3)',
                    }}
                  >
                    {l.label}
                  </a>
                  {copy.rich && i < copy.rich.goDeeper.links.length - 1 && (
                    <span aria-hidden="true" style={{ color: GHOST }}>
                      ·
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default VerticalFeatures;
